/** Small shared pieces. Nothing clever — just consistent. */
import {
  createContext, useCallback, useContext, useEffect, useId, useMemo, useState,
  type ReactNode,
} from 'react';

/* --------------------------------------------------------------- feedback */

export function Spinner() {
  return <span className="spinner" role="status" aria-label="Loading" />;
}

export function Loading({ what = 'Loading' }: { what?: string }) {
  return <div className="loading"><Spinner /> {what}…</div>;
}

export function ErrorNote({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : String(error);
  const details = (error as { fieldErrors?: Record<string, string[]> })?.fieldErrors;
  const fields = details ? Object.entries(details) : [];
  return (
    <div className="banner banner--error" role="alert">
      <strong>{message}</strong>
      {fields.length > 0 && (
        <ul style={{ margin: '.4rem 0 0', paddingLeft: '1.1rem' }}>
          {fields.map(([field, msgs]) => (
            <li key={field}><b>{field}</b>: {msgs.join('; ')}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <h3>{title}</h3>
      {children && <p className="small">{children}</p>}
    </div>
  );
}

/* ------------------------------------------------------------------ pills */

export function Pill(
  { tone = 'quiet', children }: { tone?: 'quiet' | 'good' | 'warn' | 'bad' | 'info'; children: ReactNode },
) {
  const cls = tone === 'info' ? 'pill' : `pill pill--${tone}`;
  return <span className={cls}>{children}</span>;
}

const STATUS_TONE: Record<string, 'good' | 'warn' | 'bad' | 'quiet'> = {
  active: 'good', confirmed: 'good', final: 'good', paid: 'good', signed: 'good',
  complete: 'good', held: 'warn', pending: 'warn', open: 'warn', enquiry: 'warn',
  in_progress: 'warn', waitlist: 'warn', required: 'warn', assigned: 'warn',
  cancelled: 'bad', withdrawn: 'bad', forfeit: 'bad', lapsed: 'bad', declined: 'bad',
  expired: 'bad', void: 'bad',
};

export function Status({ value }: { value: string }) {
  const tone = STATUS_TONE[value] ?? 'quiet';
  return <Pill tone={tone}>{value.replace(/_/g, ' ')}</Pill>;
}

/* ------------------------------------------------------------------ cards */

export function Card(
  { title, actions, children, flush }:
  { title?: ReactNode; actions?: ReactNode; children: ReactNode; flush?: boolean },
) {
  return (
    <section className="card">
      {(title || actions) && (
        <header className="card__head">
          {typeof title === 'string' ? <h2>{title}</h2> : title}
          {actions && <div className="spacer" />}
          {actions}
        </header>
      )}
      <div className={flush ? 'card__body card__body--flush' : 'card__body'}>{children}</div>
    </section>
  );
}

export function Stat(
  { label, value, note, alert }:
  { label: string; value: ReactNode; note?: ReactNode; alert?: boolean },
) {
  return (
    <div className={alert ? 'card stat stat--alert' : 'card stat'}>
      <div className="stat__label">{label}</div>
      <div className="stat__value">{value}</div>
      {note && <div className="stat__note">{note}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ forms */

interface FieldProps {
  label: string;
  error?: string[] | string;
  hint?: ReactNode;
  children: (id: string) => ReactNode;
}

export function Field({ label, error, hint, children }: FieldProps) {
  const id = useId();
  const messages = Array.isArray(error) ? error : error ? [error] : [];
  return (
    <div className={messages.length ? 'field field--bad' : 'field'}>
      <label htmlFor={id}>{label}</label>
      {children(id)}
      {hint && <span className="field__hint">{hint}</span>}
      {messages.map((m) => <span className="field__error" key={m}>{m}</span>)}
    </div>
  );
}

export function TextField(
  { label, value, onChange, type = 'text', required, error, hint, placeholder }:
  {
    label: string; value: string; onChange: (v: string) => void;
    type?: string; required?: boolean; error?: string[] | string;
    hint?: ReactNode; placeholder?: string;
  },
) {
  return (
    <Field label={label} error={error} hint={hint}>
      {(id) => (
        <input
          id={id} type={type} value={value} required={required} placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </Field>
  );
}

/**
 * Multi-line text. Same furniture as TextField — label, hint, error — because
 * a waiver's clauses are edited in one of these and they should not look like
 * a different kind of thing from the fields beside them.
 */
export function TextAreaField(
  { label, value, onChange, rows = 4, error, hint, placeholder }:
  {
    label: string; value: string; onChange: (v: string) => void; rows?: number;
    error?: string[] | string; hint?: ReactNode; placeholder?: string;
  },
) {
  return (
    <Field label={label} error={error} hint={hint}>
      {(id) => (
        <textarea
          id={id} value={value} rows={rows} placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </Field>
  );
}

export function SelectField<T extends string>(
  { label, value, onChange, options, error, hint }:
  {
    label: string; value: T; onChange: (v: T) => void;
    options: ReadonlyArray<{ value: T; label: string }>;
    error?: string[] | string; hint?: ReactNode;
  },
) {
  return (
    <Field label={label} error={error} hint={hint}>
      {(id) => (
        <select id={id} value={value} onChange={(e) => onChange(e.target.value as T)}>
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      )}
    </Field>
  );
}

export function CheckField(
  { label, checked, onChange, hint }:
  { label: string; checked: boolean; onChange: (v: boolean) => void; hint?: ReactNode },
) {
  return (
    <div className="field">
      <label className="check">
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        {label}
      </label>
      {hint && <span className="field__hint">{hint}</span>}
    </div>
  );
}

/* ------------------------------------------------------------------ modal */

export function Modal(
  { title, onClose, children, footer, wide, full }:
  {
    title: string; onClose: () => void; children: ReactNode; footer?: ReactNode;
    wide?: boolean;
    /** Full width, for a screen that lays panes out side by side. */
    full?: boolean;
  },
) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        className={full ? 'modal modal--full' : wide ? 'modal modal--wide' : 'modal'}
        role="dialog" aria-modal="true" aria-label={title}
      >
        <header className="modal__head">
          <h2>{title}</h2>
          <button className="modal__close" type="button" onClick={onClose} aria-label="Close">&times;</button>
        </header>
        <div className="modal__body">{children}</div>
        {footer && <footer className="modal__foot">{footer}</footer>}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ tabs */

export function Tabs<T extends string>(
  { tabs, active, onChange }:
  { tabs: ReadonlyArray<{ id: T; label: string; count?: number }>; active: T; onChange: (id: T) => void },
) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((t) => (
        <button
          key={t.id} type="button" role="tab" aria-selected={t.id === active}
          onClick={() => onChange(t.id)}
        >
          {t.label}{t.count !== undefined && ` (${t.count})`}
        </button>
      ))}
    </div>
  );
}

/* ----------------------------------------------------------------- toasts */

interface Toast { id: number; message: string; tone: 'good' | 'bad' | 'plain' }
interface ToastApi {
  success: (m: string) => void;
  error: (m: string) => void;
  show: (m: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback((message: string, tone: Toast['tone']) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, message, tone }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 5200);
  }, []);

  const api = useMemo<ToastApi>(() => ({
    success: (m) => push(m, 'good'),
    error: (m) => push(m, 'bad'),
    show: (m) => push(m, 'plain'),
  }), [push]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toasts" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={t.tone === 'plain' ? 'toast' : `toast toast--${t.tone}`}>
            <span>{t.message}</span>
            <button type="button" onClick={() => setToasts((x) => x.filter((y) => y.id !== t.id))}
              aria-label="Dismiss">&times;</button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

/** Turns an API error into a toast message, including field detail. */
export function errorMessage(err: unknown): string {
  if (err instanceof Error) {
    const fields = (err as { fieldErrors?: Record<string, string[]> }).fieldErrors;
    if (fields && Object.keys(fields).length) {
      const first = Object.entries(fields)[0]!;
      return `${err.message} (${first[0]}: ${first[1].join('; ')})`;
    }
    return err.message;
  }
  return 'Something went wrong.';
}
